import { ReactNode } from 'react'
import Popover from '@mui/material/Popover'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import FullScreenSheet from '../../../display/FullScreenSheet'

type FlyoutSurfaceProps = {
  /** The element the desktop popover anchors to; also drives the open state. */
  readonly anchorElement: HTMLElement | null
  readonly onClose: () => void
  readonly children: ReactNode
}

/**
 * The host the app-menu flyout body renders into. The body is identical on both
 * surfaces; only the host differs - an anchored popover on desktop, a
 * full-screen dialog (with a back chevron to leave without choosing a
 * destination) below smxl.
 */
const FlyoutSurface = ({
  anchorElement,
  onClose,
  children,
}: FlyoutSurfaceProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('smxl'))

  if (isMobile) {
    return (
      <FullScreenSheet
        open={Boolean(anchorElement)}
        onClose={onClose}
        closeAriaLabel="Close profile menu"
      >
        {children}
      </FullScreenSheet>
    )
  }

  return (
    <Popover
      anchorEl={anchorElement}
      open={Boolean(anchorElement)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // Without this, the underlying MUI Modal sets `overflow: hidden` on the
      // body while open, which on Windows removes the always-present scrollbar
      // (`body { overflow-y: scroll }`) and shifts the layout. Scroll locking
      // isn't needed here: the popover anchors to the fixed topbar, so page
      // scrolling can't move it out of alignment.
      disableScrollLock
      slotProps={{ paper: { sx: { width: 340, marginTop: 1 } } }}
    >
      {children}
    </Popover>
  )
}

export default FlyoutSurface
